import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ENDPOINTS } from 'app/core/constants/endpoints';
import { SKIP_AUTH_REFRESH } from 'app/core/interceptors/http-context-tokens';
import { ApiService } from 'app/core/services/api.service';
import { BaseResponse } from 'app/shared/models/base-response';
import { ErrorHandlerService } from 'app/shared/services/error-handler.service';
import { DataStorageService } from 'app/shared/utils/data-storage-service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AgentLoginRequest } from '../models/agent-login-request.model';
import { AgentLoginResponse } from '../models/agent-login-response.model';
import { AgentRegisterResponse } from '../models/agent-register-response.model';
import { User } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthService extends ErrorHandlerService {
  constructor(
    private apiService: ApiService,
    private dataStorageService: DataStorageService
  ) {
    super();
  }

  /**
   * * Register New Agent
   * @param payload AgentRegisterRequest
   * @returns Observable<AgentRegisterResponse>
   */
  registerAgent(payload: FormData): Observable<AgentRegisterResponse> {
    return this.apiService.post<AgentRegisterResponse>(
      ENDPOINTS.AGENT.REGISTER,
      payload,
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
    );
  }

  /**
   * * Login Agent
   * @param payload AgentLoginRequest
   * @returns Observable<AgentLoginResponse>
   */
  loginAgent(payload: AgentLoginRequest): Observable<AgentLoginResponse> {
    return this.apiService.post<AgentLoginResponse>(
      ENDPOINTS.AGENT.LOGIN,
      payload,
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
    );
  }

  /**
   * * Verify Email
   * @param token string
   * @returns Observable<BaseResponse>
   */
  verifyEmail(token: string): Observable<BaseResponse> {
    return this.apiService.get<BaseResponse>(
      ENDPOINTS.AUTH.VERIFY_EMAIL,
      { token: token },
      new HttpContext().set(SKIP_AUTH_REFRESH, true)
    );
  }

  /**
   * * Resend Verification Email
   * @returns Observable<BaseResponse>
   */
  resendVerificationEmail(email: string): Observable<BaseResponse> {
    return this.apiService.post<BaseResponse>(
      ENDPOINTS.AUTH.RESEND_VERIFICATION,
      { email: email },
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
    );
  }

  /**
   * * Set Password for First Time
   * @param token string
   * @param password string
   * @param password_confirmation string
   * @returns Observable<BaseResponse>
   */
  setPassword(token: string, password: string, password_confirmation: string): Observable<BaseResponse> {
    return this.apiService.post<BaseResponse>(
      ENDPOINTS.AUTH.SET_PASSWORD,
      {
        token: token,
        password: password,
        password_confirmation: password_confirmation
      },
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
    );
  }

  /**
   * * Logout Agent
   * * This method will clear the session and local storage.
   * * It will also invalidate the token on the server.
   * @returns Observable<BaseResponse>
   */
  logout(): Observable<BaseResponse> {
    return this.apiService.post<BaseResponse>(ENDPOINTS.AUTH.LOGOUT, null);
  }

  refreshToken(): Observable<any> {
    return this.apiService
      .post<any>(ENDPOINTS.AGENT.REFRESH, {}, {
        context: new HttpContext().set(SKIP_AUTH_REFRESH, true),
      })
      .pipe(
      tap((response) => {
        if (response.data?.token) {
          if (this.dataStorageService.get('token')) {
            this.dataStorageService.set('token', response.data.token);
          } else {
            sessionStorage.setItem('token', response.data.token);
          }
        }
      })
    );
  }

  clearSession(): void {
    sessionStorage.clear();
    this.dataStorageService.clearAll();
  }

  storeUserData(
    token: string,
    userData: User,
    email: string,
    rememberMe: boolean
  ): void {
    if (rememberMe) {
      this.dataStorageService.set('token', token);
      this.dataStorageService.set('user', JSON.stringify(userData));
      this.dataStorageService.set('email', email);
      return;
    } else {
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.setItem('email', email);
    }
  }

  getUser(): User {
    const user = sessionStorage.getItem('user');
    if (user) {
      return JSON.parse(user);
    } else {
      const user = this.dataStorageService.get('user');
      return user ? JSON.parse(user) : ({} as User);
    }
  }

  getBranches() {
    const userStr =
      sessionStorage.getItem('user') || this.dataStorageService.get('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.agent && user.agent.branches ? user.agent.branches : [];
    }
    return [];
  }

  getToken(): string | null {
    return (
      this.dataStorageService.get('token') || sessionStorage.getItem('token')
    );
  }

  isLoggedIn(): boolean {
    return this.getToken() !== null;
  }
}
